import React from 'react';
export type LegalBlock = {
  type: 'p';
  text: string;
} | {
  type: 'ul';
  items: string[];
} | {
  type: 'table';
  headers: [string, string];
  rows: [string, string][];
};
export type LegalSection = {
  heading: string;
  blocks: LegalBlock[];
};
export type LegalDoc = {
  title: string;
  updated: string;
  intro?: string;
  sections: LegalSection[];
  contact: string;
};

// Parses **bold** markers into <strong> spans without pulling in a markdown lib.
function renderInline(text: string) {
  const parts = text.split('**');
  return parts.map((part, i) => i % 2 === 1 ? <strong key={i} className="font-semibold text-brand-textPrimary" data-id="element-304" data-mp="eehw0b6bsuj4">
        {part}
      </strong> : <React.Fragment key={i} data-id="element-305" data-mp="9d10kv6m209x">{part}</React.Fragment>);
}
function BlockView({
  block
}: {
  block: LegalBlock;
}) {
  if (block.type === 'p') {
    return <p className="text-brand-textSecondary leading-relaxed mb-4" data-id="element-306" data-mp="1kuk7axjegld8">
        {renderInline(block.text)}
      </p>;
  }
  if (block.type === 'ul') {
    return <ul className="list-disc pl-6 space-y-2 mb-4 text-brand-textSecondary" data-id="element-307" data-mp="tmz862n5rlpc">
        {block.items.map((item, i) => <li key={i} className="leading-relaxed" data-id="element-308" data-mp="13j2e7jl4s4hk" data-mpi={i}>
            {renderInline(item)}
          </li>)}
      </ul>;
  }
  if (block.type === 'table') {
    return <div className="overflow-x-auto mb-6 rounded-xl border border-brand-border" data-id="element-309" data-mp="18fj9qyuntl4o">
        <table className="w-full text-left border-collapse" data-id="element-310" data-mp="avyarj1ory7w">
          <thead data-id="element-311" data-mp="fu9ow5z0kuvx">
            <tr className="border-b border-brand-border" data-id="element-312" data-mp="ftq48hhzbse7">
              <th className="px-4 py-3 font-heading font-semibold text-brand-textPrimary text-sm" data-id="element-313" data-mp="krro34n8bvqq">
                {block.headers[0]}
              </th>
              <th className="px-4 py-3 font-heading font-semibold text-brand-textPrimary text-sm" data-id="element-314" data-mp="pkcf5yer3uqk">
                {block.headers[1]}
              </th>
            </tr>
          </thead>
          <tbody data-id="element-315" data-mp="3t6jr78d2o8mw">
            {block.rows.map((row, i) => <tr key={i} className="border-t border-brand-border" data-id="element-316" data-mp="3l6wwr3ecbd5c" data-mpi={i}>
                <td className="px-4 py-3 text-brand-textSecondary text-sm align-top" data-id="element-317" data-mp="3l52e5vqjjx4f">
                  {row[0]}
                </td>
                <td className="px-4 py-3 text-brand-textSecondary text-sm align-top" data-id="element-318" data-mp="3uxhu6nia2bmo">
                  {row[1]}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>;
  }
  return null;
}

// Shared renderer for the three legal pages (Privacy, Terms, Refund). Keeping
// the layout in one place means the three pages always look consistent and a
// future style tweak only happens once.
export function LegalPage({
  doc
}: {
  doc: LegalDoc;
}) {
  return <div className="py-16 md:py-24 bg-brand-bg" data-id="element-319" data-mp="377e3fgq6u6d3">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-320" data-mp="379qj1n8h44mz">
        <h1 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-2 font-heading" data-id="element-321" data-mp="rfge5r2npulf">
          {doc.title}
        </h1>
        <p className="text-sm text-brand-textSecondary mb-10" data-id="element-322" data-mp="mjj0xtmox4cv">{doc.updated}</p>
        {doc.intro && <p className="text-brand-textSecondary leading-relaxed mb-10" data-id="element-323" data-mp="u30p5kavqifr">
            {doc.intro}
          </p>}
        {doc.sections.map((section, i) => <section key={i} className="mb-10" data-id="element-324" data-mp="l1knc0y4ron5" data-mpi={i}>
            <h2 className="text-xl md:text-2xl font-bold text-brand-textPrimary mb-4 font-heading" data-id="element-325" data-mp="p1uuhgxnsl3l">
              {section.heading}
            </h2>
            {section.blocks.map((block, j) => <BlockView key={j} block={block} data-id="element-326" data-mp="1hqijnnlj3umf" data-mpi={j} />)}
          </section>)}
        <div className="pt-8 mt-4 border-t border-brand-border text-sm text-brand-textSecondary" data-id="element-327" data-mp="1hscta5rco0b7">
          {doc.contact}
        </div>
      </div>
    </div>;
}